import { FC } from 'react';
import { GraduationCap } from 'lucide-react';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { DegreeType } from '@teachur/http-client';
import type { Degree, UserDegree } from '@teachur/http-client';

import { Button } from '@new/common/ui';
import { Loader } from '@new/common/ui/Loader/Loader';
import { cn } from '@new/lib';

import { ConfirmSwitchDegreeModal } from './ConfirmSwitchDegreeModal';
import { useStudentDegreeSwitch } from './useStudentDegreeSwitch';

interface StudentDegreesTabProps {
  studentId: number;
  enrolledDegrees: UserDegree[];
  onSwitched?: () => void;
}

const degreeTypeLabel = (type: string | undefined): string => {
  if (type === DegreeType.Master) return "Master's";
  if (type === DegreeType.Bachelor) return "Bachelor's";
  if (type === DegreeType.Associate) return 'Associate';
  return type ?? '';
};

export const StudentDegreesTab: FC<StudentDegreesTabProps> = ({
  studentId,
  enrolledDegrees,
  onSwitched,
}) => {
  const {
    isAdmin,
    isLoadingDegrees,
    currentDegree,
    selectableDegrees,
    pendingDegree,
    isPending,
    pendingChangesSubscription,
    requestSwitch,
    cancelSwitch,
    confirmSwitch,
  } = useStudentDegreeSwitch({ studentId, enrolledDegrees, onSwitched });

  if (isLoadingDegrees) {
    return <Loader />;
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Current enrollment */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <GraduationCap className="h-5 w-5 text-(--primary-O900)" />
          <h3 className="[font-family:var(--font-arvo)] leading-tight tracking-tight [font-size:var(--fs-h5)] m-0! font-bold text-(--neutral-black-NB1000)!">
            Current Degree
          </h3>
        </div>
        {currentDegree ? (
          <div className="rounded-xl border border-(--primary-O600) bg-(--primary-O100) p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="[font-family:var(--font-montserrat)] font-semibold text-(--neutral-black-NB1000)!">
                {currentDegree.name}
              </span>
              <span className="rounded-full bg-(--neutral-white-NW1000) px-3 py-1 text-xs font-medium text-(--neutral-black-NB700)">
                {degreeTypeLabel(currentDegree.type)}
              </span>
            </div>
          </div>
        ) : (
          <p className="[font-size:var(--fs-p3)]! text-(--neutral-black-NB700)! m-0!">
            This student is not enrolled in any degree.
          </p>
        )}
      </div>

      {isAdmin && (
        <div className="flex flex-col gap-2">
          <h4 className="[font-family:var(--font-montserrat)]! [font-size:var(--fs-l3)]! font-semibold m-0! text-(--neutral-black-NB1000)!">
            Switch to another degree
          </h4>
          <p className="[font-size:var(--fs-p3)]! text-(--neutral-black-NB700)! m-0!">
            Switching replaces the student&apos;s current degree. If the new degree crosses the
            master&apos;s boundary, the subscription plan changes to match.
          </p>

          {selectableDegrees.length === 0 ? (
            <p className="[font-size:var(--fs-p3)]! text-(--neutral-black-NB700)! m-0! pt-2">
              No other degrees are available to switch to.
            </p>
          ) : (
            <div className="mt-1 rounded-xl border bg-(--neutral-black-NB100)/50 p-3">
              <ScrollArea.Root className="overflow-hidden rounded-lg" type="auto">
                <ScrollArea.Viewport className="max-h-[50vh] w-full">
                  <div className="flex flex-col gap-2">
                    {selectableDegrees.map((degree: Degree) => (
                      <div
                        key={degree.id}
                        className={cn(
                          'flex items-center justify-between gap-3 rounded-lg border bg-(--neutral-white-NW1000) p-3',
                          'border-(--neutral-black-NB100)'
                        )}
                      >
                        <div className="flex flex-col">
                          <span className="[font-family:var(--font-montserrat)] text-sm font-medium text-(--neutral-black-NB1000)!">
                            {degree.name}
                          </span>
                          <span className="text-xs text-(--neutral-black-NB700)">
                            {degreeTypeLabel(degree.type)} degree
                          </span>
                        </div>
                        <Button variant="primary" size="sm" onClick={() => requestSwitch(degree)}>
                          Switch
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea.Viewport>
                <ScrollArea.Scrollbar
                  orientation="vertical"
                  className="flex w-2.5 bg-transparent p-0.5 transition-colors hover:bg-(--neutral-black-NB100)/50"
                >
                  <ScrollArea.Thumb className="flex-1 cursor-pointer rounded-full bg-(--neutral-black-NB300) transition-colors hover:bg-(--neutral-black-NB500)" />
                </ScrollArea.Scrollbar>
                <ScrollArea.Corner />
              </ScrollArea.Root>
            </div>
          )}
        </div>
      )}

      {pendingDegree && (
        <ConfirmSwitchDegreeModal
          isOpen={!!pendingDegree}
          fromDegreeName={currentDegree?.name}
          toDegreeName={pendingDegree.name}
          changesSubscription={pendingChangesSubscription}
          toIsMaster={pendingDegree.type === DegreeType.Master}
          isPending={isPending}
          onConfirm={confirmSwitch}
          onClose={cancelSwitch}
        />
      )}
    </div>
  );
};
